var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { nanoid } from 'nanoid';
import { $mobx } from 'mobx';
import { child, primitive, serialize, withDefault } from '../../utils/serializr/serializr';
import { EntityContext } from '../context/EntityContext';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { EntityStore } from './modules/EntityStore';
import { resetOnCopy, resetOnCopy_id } from '../../utils/serializr/schema/middle/resetOnCopy';
import { resetToInitial } from '../../utils/serializr/schema/middle/resetToInitial';
import { onlyDbEntities } from '../../utils/serializr/schema/middle/onlyDbEntities';
import { deserialize } from '../../utils/serializr/core/deserialize';
import { EntityTemp } from './modules/EntityTemp';
import { EntityMutation } from './modules/EntityMutation';
import { withInit } from '../../utils/serializr/schema/middle/withInit';
import { EntityInfo } from './modules/EntityInfo';
import { EntitySerialize } from './modules/EntitySerialize';
import { EntityObservables } from './modules/EntityVars';
import { editable, editObservable } from '../../utils/mobx/decorators/editable';
import { runtime } from '../runtime/Runtime';
import { UTCdate } from '../../utils/serializr/schema/inner/UTCdate';
import { ensureString } from '../../utils/helper/lodash_replacements';
export class Entity {
    type = 'entity';
    parent;
    serialized_origin;
    owningModel;
    id_raw;
    mutability;
    entityStore;
    constructor({ parent }) {
        editObservable(this);
        this.parent = parent;
    }
    get mobx() {
        return this[$mobx];
    }
    get id() {
        return this.id_raw;
    }
    set id(id) {
        this.id_raw = id;
    }
    get id_string() {
        return ensureString(this.id);
    }
    get tid() {
        return `${this.type}_${this.id}`;
    }
    set tid(tid) { }
    get updatedAt() {
        return this.entityObservables.updatedAt;
    }
    set updatedAt(updatedAt) {
        this.entityObservables.updatedAt = updatedAt;
    }
    get document_version() {
        return this.entityObservables.document_version;
    }
    set document_version(v) {
        this.entityObservables.document_version = v;
    }
    get entityObservables() {
        return new EntityObservables(this);
    }
    get entityInfo() {
        return new EntityInfo(this);
    }
    get entityEdit() {
        return new runtime.solidPackages.Edit.classes.EntityEdit(this);
    }
    get entitySerialize() {
        return new EntitySerialize(this);
    }
    get importingChildren() {
        return [];
    }
    get exportingChildren() {
        return [];
    }
    get context() {
        return new EntityContext(this);
    }
    get entityTemp() {
        return new EntityTemp(this);
    }
    get entityMutation() {
        return new EntityMutation(this);
    }
    get model() {
        return runtime.model.getNewModel(this);
    }
    get serialized() {
        return this.entitySerialize.serialized;
    }
    set serialized(ser) {
        this.entitySerialize.serialized = ser;
    }
    get objectIdOfSavingEntity() {
        return this.entityStore.objectIdOfSavingEntity;
    }
    get syncId() {
        return this.entityInfo.objectId;
    }
    get stripped() {
        return serialize(this, this.constructor.stripInfo);
    }
    static fromSerialized(ser = {}) {
        const EntityImpl = runtime.class.serialized_to_EntityClass(ser);
        return deserialize(EntityImpl, ser);
    }
    static get serializeInfo() {
        return {
            props: {
                id: withInit(resetOnCopy(primitive, resetOnCopy_id), () => nanoid(6)),
                type: resetToInitial(primitive),
                entityStore: withDefault(child(EntityStore), {}, true),
                document_version: onlyDbEntities(withInit(primitive, 1)),
                createdAt: onlyDbEntities(withInit(UTCdate(), () => runtime.runtimeClock.currentMySqlUTCTimestamp)),
                updatedAt: onlyDbEntities(withInit(UTCdate(), () => runtime.runtimeClock.currentMySqlUTCTimestamp)),
                accessedAt: onlyDbEntities(withInit(UTCdate(), () => runtime.runtimeClock.currentMySqlUTCTimestamp)),
            },
        };
    }
    static get stripInfo() {
        return {
            props: {
                type: true,
            },
        };
    }
}
__decorate([
    editable('ref')
], Entity.prototype, "parent", void 0);
__decorate([
    editable()
], Entity.prototype, "id_raw", void 0);
__decorate([
    memoize
], Entity.prototype, "entityObservables", null);
__decorate([
    memoize
], Entity.prototype, "entityInfo", null);
__decorate([
    memoize
], Entity.prototype, "entityEdit", null);
__decorate([
    memoize
], Entity.prototype, "entitySerialize", null);
__decorate([
    memoize
], Entity.prototype, "context", null);
__decorate([
    memoize
], Entity.prototype, "entityTemp", null);
__decorate([
    memoize
], Entity.prototype, "entityMutation", null);
__decorate([
    memoize
], Entity.prototype, "model", null);
__decorate([
    memoize
], Entity, "serializeInfo", null);
__decorate([
    memoize
], Entity, "stripInfo", null);
